import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import { useInfiniteCache } from '@/data/hook/query/v2/useCacheManager';
import useListService from '@/data/hook/useListService';
import {
  createLessonIntoBlock,
  deleteLessonIntoBlock,
} from '@/data/services/lessonBlocksServices';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import { updateLessonOrder } from '@/data/services/lessonServices';
import alert from '@/utils/shared/alert';
import { mountKey } from '@/utils/courses';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';

type UseUnitLessonsProps = {
  slug: string;
  unitId: number;
};

type CreateLessonData = {
  name: string;
  description?: string;
  order: number;
};

const useLessons = ({ slug, unitId }: UseUnitLessonsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.lessonsTab',
  });
  const { tree } = useTreeCourses();

  const lessonsQueryKey = lessonBlocksQueryKeys.listLessons(slug, unitId, {
    pageSize: ORDENABLE_PAGE_SIZE,
  });

  const infiniteCacheManager = useInfiniteCache(
    lessonBlocksQueryKeys.listLessons(slug, unitId)._ctx.infinity.queryKey,
  );
  const {
    results: lessons,
    isInitialLoading,
    cacheManager,
  } = useListService({
    ...lessonsQueryKey,
    enabled: !!slug && !!unitId,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateLessonData) =>
      createLessonIntoBlock(slug, unitId, data),
    onSuccess: lesson => {
      cacheManager.add(lesson);
      infiniteCacheManager.add(lesson);
      const parentKey = mountKey({ type: 'unit', id: unitId });
      tree?.current.upsert(
        {
          id: mountKey({ type: 'lesson', id: lesson.id }),
          name: lesson.name,
          type: 'lesson',
          realId: lesson.id,
        },
        parentKey,
      );

      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonId: number) =>
      deleteLessonIntoBlock(slug, unitId, lessonId),
    onSuccess: (_, lessonId) => {
      cacheManager.remove(lessonId);
      infiniteCacheManager.remove(lessonId);
      tree?.current.delete(mountKey({ type: 'lesson', id: lessonId }));
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: (lessonIds: number[]) => {
      const reorderedLessons = reorder(lessons, lessonIds);
      cacheManager.set(reorderedLessons);
      infiniteCacheManager.set(reorderedLessons);
    },
    mutationFn: (lessonIds: number[]) =>
      updateLessonOrder(
        slug,
        unitId,
        lessonIds.map(id => ({ id })),
      ),
    onSuccess: () => {
      alert.success(t('reorderSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      infiniteCacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  return {
    lessons,
    isLoading: isInitialLoading,
    cacheManager,
    createLesson: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteLesson: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderLessons: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
  };
};

export default useLessons;
